<template>
  <div class="library__article">
    <p>
      <VisibleText>Go to </VisibleText>
      <router-link to="/library">
        <VisibleText>Library</VisibleText>
      </router-link>
    </p>

    <h1 class="library__h1">
      <VisibleText>Measuring What Matters Inside</VisibleText>
    </h1>

    <div class="library__heading-line"></div>

    <p>
      <VisibleText>2026-04-01</VisibleText>
    </p>

    <img
      class="library__image"
      style="max-height: 15rem; max-width: 95%"
      src="/assets/landing/placeholder_internal_services.jpg"
      alt=""
    />

    <h2 class="library__h2">
      <VisibleText>
        How a two-person team unlocked three years of dormant internal service data — and found what
        no performance report had ever shown
      </VisibleText>
    </h2>

    <p>
      <VisibleText>
        1,694 open-ended feedback responses. Eleven corporate service lines. Three years of
        accumulated data, sitting largely unanalyzed. A two-person team with no dedicated data
        science capacity and no cross-unit analytical framework.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        This is where a provincial Crown Corporation's internal service intelligence story begins.
        The data had been collected. The survey was well-designed. But the open-text responses — up
        to 5,082 individual text fields covering accounts payable, enterprise IT, pay and benefits,
        procurement, translation, managed print, supply chain, capital engineering, and laundry
        services — had never been fully utilized. Their organizational intelligence was untapped.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        Using the ResearchAI hybrid methodology, that same two-person team built, validated, and
        operated a complete internal service intelligence system in six months. What they produced
        was not a report. It was a capability — a validated, continuously operable analytical
        function that the organization now runs independently, and that has produced findings that
        three years of performance reporting had never surfaced.
      </VisibleText>
    </p>

    <h3 class="library__h3">
      <VisibleText>The Problem That Performance Reporting Cannot Solve</VisibleText>
    </h3>

    <p>
      <VisibleText>
        The challenge facing this organization's Strategic Services unit is common across the
        Canadian public sector: a rich evidence base without the tools and methodology to fully
        exploit it. Each service line reported in isolation. There was no cross-unit framework to
        identify systemic patterns versus service-specific issues. Failure demand — contacts
        generated not by new client needs but by prior service failures that went unresolved — was
        entirely invisible. Reports were produced but improvement accountability was undefined, and
        the cycle was never closed.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        The gap was not a data gap. It was a methodological gap: no validated framework to unlock
        what the data contained, and no structure to connect findings to decisions. ResearchAI was
        engaged not to produce a report but to transfer the capability to produce one — and the next
        one, and the one after that, without external dependency.
      </VisibleText>
    </p>

    <h3 class="library__h3">
      <VisibleText>What the Analysis Found</VisibleText>
    </h3>

    <p>
      <VisibleText>
        The nine-stage ResearchAI methodology was applied to the full 2022 dataset: context framing,
        model construction, data audit, dictionary building, validation, cross-unit analysis,
        insight development, audience-specific reporting, and action register construction. Each
        stage built on the last. The result was a cross-unit coverage matrix — all eleven service
        lines against nine themes simultaneously — produced for the first time in the organization's
        history. Five findings stand out.
      </VisibleText>
    </p>

    <h2 class="library__h2">
      <VisibleText>Finding What It Showed</VisibleText>
    </h2>

    <table>
      <thead>
        <tr>
          <th>#</th>
          <th>Finding</th>
          <th>What It Showed</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>1</td>
          <td>Timeliness is systemic, not operational</td>
          <td>
            Dominant theme in every single service line — from IT (47%) to Pay &amp; Benefits (46%)
            to Translation (47%). The cross-unit consistency identified this as a design problem,
            not a capacity problem. Adding staff to any individual service line would not address a
            root cause embedded in the service architecture.
          </td>
        </tr>
        <tr>
          <td>2</td>
          <td>Staff are the asset. Systems are the problem.</td>
          <td>
            Staff Competence was the lowest or second-lowest theme in every service line (cross-unit
            average ~ 10%). Yet individual staff were named by name in unsolicited positive feedback
            across multiple lines. Internal clients are not dissatisfied with people. They are
            absorbing the cost of systems that are slow, hard to navigate, and prone to generating
            contacts that should not exist.
          </td>
        </tr>
        <tr>
          <td>3</td>
          <td>Failure demand is present and measurable</td>
          <td>
            8.3% of all improvement responses — 121 of 1,457 — contained explicit failure demand
            language. Pay &amp; Benefits: 10%. Supply Chain: 12%. AP: 28%. These are contacts
            generated by prior service failures, not new needs. The productivity cost had never
            previously been quantifiable.
          </td>
        </tr>
        <tr>
          <td>4</td>
          <td>Two service lines required structural escalation</td>
          <td>
            IT had an Ease of Access coverage rate of 60% — the highest single theme score in the
            entire dataset. AP had a Process score of 50% and a 28% failure demand rate. Both
            conditions were beyond service line manager authority and were classified as Tier 3
            escalation items requiring director-level structural review.
          </td>
        </tr>
        <tr>
          <td>5</td>
          <td>One service line had crossed the accountability threshold</td>
          <td>
            A zero failure demand rate ordinarily signals a well-functioning service. Here it
            signalled the opposite: a service so chronically dysfunctional that internal clients had
            stopped reporting failures because they had stopped expecting anything to change. The
            verbatims described linen shortages that had persisted since a supplier transition,
            nursing staff leaving patient bedsides to source supplies from other wards, and formal
            escalations that produced no response. The 3% positive signal rate was the lowest in the
            entire dataset. This was not a service improvement opportunity. It was an accountability
            failure with direct patient care consequences — classified as a patient safety adjacency
            and escalated immediately as a Tier 3 item requiring leadership attention within 30
            days.
          </td>
        </tr>
      </tbody>
    </table>

    <p>
      <VisibleText>
        "40% of Timeliness responses co-occurred with Ease of Access difficulty. The primary driver
        of internal client waiting was not insufficient staff. It was systems and processes that
        were complicated to navigate and prone to requiring re-work."
      </VisibleText>
    </p>

    <h2 class="library__h2">
      <VisibleText>From Findings to Action: The Three-Tier Register</VisibleText>
    </h2>

    <p>
      <VisibleText>
        Every insight was linked to a specific decision and a specific decision-owner. The action
        register separated what service line managers could do within 30 to 60 days from what
        required director authority, and from what required immediate escalation. Eight Tier 1
        actions were defined, including a standard 2-business-day acknowledgement for all internal
        client contacts across the eight service lines with communication gaps — with a predicted
        measurable reduction in the 8.3% failure demand rate in the next survey cycle. Five Tier 2
        reviews were assigned to directors. Two Tier 3 items — Laundry and IT — required leadership
        attention within 30 days, not at the next quarterly review.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        Every action was linked to a predicted movement in a specific theme coverage rate in the
        next survey cycle. Improvement becomes testable. The feedback loop closes.
      </VisibleText>
    </p>

    <h3 class="library__h3">
      <VisibleText>What the Team Can Now Do</VisibleText>
    </h3>

    <table>
      <thead>
        <tr>
          <th>Before ResearchAI</th>
          <th>After ResearchAI</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Open-text responses reviewed manually by sample</td>
          <td>1,694 responses fully analyzed with validated dictionary coverage</td>
        </tr>
        <tr>
          <td>Single service line reports produced in isolation</td>
          <td>Cross-unit comparison: all 11 lines against 9 themes simultaneously</td>
        </tr>
        <tr>
          <td>No failure demand identification</td>
          <td>Failure demand baseline established by service line; improvement predictions made</td>
        </tr>
        <tr>
          <td>Reports delivered; no action structure</td>
          <td>
            Three-tier action register: 8 Tier 1 actions, 5 Tier 2 reviews, 2 Tier 3 escalations
          </td>
        </tr>
        <tr>
          <td>Dependent on external support for analysis</td>
          <td>Team of 2–3 runs full analysis cycle independently; methodology owned internally</td>
        </tr>
      </tbody>
    </table>

    <p>
      <VisibleText>
        The challenge this organization faced — rich feedback data, limited analytical capacity, no
        systematic connection between findings and action — describes the majority of shared
        services organizations in the Canadian public sector. The dictionary architecture, the
        failure demand framework, the three-tier action register, and the capability transfer model
        all apply wherever internal service quality needs to be measured, understood, and improved
        at organizational scale. The methodology is transferable. The team just has to start.
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        "The question the next survey cycle will answer is not whether the methodology works. That
        was confirmed in the baseline analysis. The question is what moved — which failure demand
        rates declined, which Tier 1 actions produced measurable theme shifts, and which structural
        interventions changed the experience of the internal clients who depend on these services."
      </VisibleText>
    </p>

    <p>
      <VisibleText>
        The full case study — including the nine-stage methodology walkthrough, complete findings
        for all eleven service lines, verbatim examples, and the full three-tier action register —
        is available in the ResearchAI library. research-ai.io/library | Next in the series: Article
        5 — What Social Media Tells You That Surveys Never Will
      </VisibleText>
    </p>

    <h4 class="library__h3">
      <VisibleText>About ResearchAI</VisibleText>
    </h4>

    <p>
      <VisibleText>
        ResearchAI (research-ai.io) is a Canadian text analytics platform developed by PigeonLine
        Ltd. It combines traditional data science with strategically applied GenAI to transform
        unstructured feedback into validated organizational intelligence. Used by federal,
        provincial, and territorial governments across Canada, and by public sector partners in the
        UK and internationally.
      </VisibleText>
    </p>

    <div class="library__textbox" style="padding: 0.5em">
      <div class="library__textbox__col">
        <img
          class="library__image library__image--nonresponsive"
          style="max-width: 4rem"
          src="/assets/landing/analyze_icon.png"
          alt=""
        />
      </div>
      <div class="library__textbox__col">
        <ul>
          <li>
            <VisibleText>
              If you are an existing user,
              <router-link to="/login">login to ResearchAI</router-link>
              and try something new. Or reach out to us at:
              <a href="mailto:hello@researchai.io">hello@researchai.io</a>
            </VisibleText>
          </li>
          <li>
            <VisibleText>
              If you are finding us for the first time,
              <router-link to="/register">register for a free ResearchAI account.</router-link>
              We will be in touch to set you up for success.
            </VisibleText>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { useHead } from '@unhead/vue';
import { computed, reactive } from 'vue';

export default {
  name: 'TrustedAISeries_InternalServices',
  setup() {
    const siteData = reactive({
      title: 'The Trusted AI Series · Measuring What Matters Inside',
      description:
        'How a two-person team unlocked three years of dormant internal service data — and found what no performance report had ever shown.',
    });

    useHead({
      title: computed(() => siteData.title),
      meta: [
        {
          name: 'description',
          content: computed(() => siteData.description),
        },
      ],
    });

    return { siteData };
  },

  methods: {
    goto(refName) {
      this.$refs[refName].scrollIntoView();
    },
  },
};
</script>
